import { ApiService } from '@/services/api'
import { AppState } from '@/stores/app-state'
import type { Project, ProjectCreate } from '@/types'
import { Toast } from '@/components/toast'

export class ProjectManager {
  private container: HTMLElement
  private apiService: ApiService
  private appState: AppState
  private toastService: Toast
  private projects: Project[] = []
  private isLoading = false
  private onProjectChangedCallback?: () => void

  constructor(
    container: HTMLElement,
    apiService: ApiService,
    appState: AppState,
    toastService: Toast,
    onProjectChangedCallback?: () => void
  ) {
    this.container = container
    this.apiService = apiService
    this.appState = appState
    this.toastService = toastService
    this.onProjectChangedCallback = onProjectChangedCallback
    
    this.render()
    this.loadProjects()
    
    // Listen for current project changes to re-render with updated data
    this.appState.subscribe('currentProject', (_project: any) => {
      console.log('🔄 ProjectManager: Current project changed, re-rendering with updated data')
      this.render()
    })
  }

  private async loadProjects(): Promise<void> {
    try {
      this.setLoading(true)
      const response = await this.apiService.listProjects()
      this.projects = response.projects
      this.render()
      
      // Auto-select first project if none selected
      if (this.projects.length > 0 && !this.appState.getCurrentProject()) {
        this.selectProject(this.projects[0])
      }
    } catch (error) {
      console.error('Failed to load projects:', error)
      this.toastService.error('Failed to load projects', 'Please check your connection and try again')
    } finally {
      this.setLoading(false)
    }
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading
    this.render()
  }

  private selectProject(project: Project): void {
    const currentProject = this.appState.getCurrentProject()
    
    // Check if actually switching projects
    if (currentProject && currentProject.id === project.id) {
      console.log(`ℹ️ Project ${project.name} already selected`)
      return
    }
    
    const startTime = Date.now()
    
    // Clear current file and selections when switching projects
    this.appState.setCurrentFile(null)
    this.appState.setSelections([])
    
    // Set new project
    this.appState.setCurrentProject(project)
    this.render()
    
    // Trigger callback to refresh workflow navigation with delay to ensure state propagation
    if (this.onProjectChangedCallback) {
      setTimeout(() => {
        this.onProjectChangedCallback!()
      }, 50) // Small delay to ensure project state is fully set
    }
    
    const duration = Date.now() - startTime
    console.log(`🔄 Project switched: ${currentProject?.name || 'None'} → ${project.name} (${duration}ms)`)
  }

  private async createProject(name: string, description?: string): Promise<void> {
    try {
      this.setLoading(true)
      const projectData: ProjectCreate = { name, description }
      const newProject = await this.apiService.createProject(projectData)
      
      this.projects.unshift(newProject)
      this.selectProject(newProject)
      this.toastService.success('Project Created', `"${name}" was created successfully`)
    } catch (error: any) {
      console.error('Failed to create project:', error)
      
      // Handle specific error types
      if (error.response?.status === 400) {
        const errorDetail = error.response?.data?.detail || ''
        if (errorDetail.includes('already exists')) {
          this.toastService.error('Project Name Taken', `A project named "${name}" already exists. Please choose a different name.`)
        } else {
          this.toastService.error('Invalid Project Data', errorDetail || 'Please check your input and try again')
        }
      } else if (error.response?.status === 401) {
        this.toastService.error('Authentication Required', 'Please log in to create projects')
      } else if (error.response?.status >= 500) {
        this.toastService.error('Server Error', 'There was a problem on our end. Please try again later.')
      } else {
        this.toastService.error('Failed to Create Project', 'Please check your connection and try again')
      }
    } finally {
      this.setLoading(false)
    }
  }

  private async renameProject(project: Project): Promise<void> {
    this.showRenameProjectDialog(project)
  }

  private async editDescription(project: Project): Promise<void> {
    this.showEditDescriptionDialog(project)
  }

  private async duplicateProject(project: Project): Promise<void> {
    this.showDuplicateProjectDialog(project)
  }

  private async deleteProject(project: Project): Promise<void> {
    this.showDeleteConfirmDialog(project)
  }

  private showRenameProjectDialog(project: Project): void {
    const dialog = document.createElement('div')
    dialog.className = 'project-dialog-overlay'
    dialog.innerHTML = `
      <div class="project-dialog">
        <div class="project-dialog-header">
          <h3>Rename Project</h3>
          <button class="project-dialog-close" type="button">&times;</button>
        </div>
        <form class="project-dialog-form">
          <div class="form-group">
            <label for="project-name">Project Name *</label>
            <input type="text" id="project-name" required maxlength="100" value="${project.name}" placeholder="Enter project name">
          </div>
          <div class="project-dialog-actions">
            <button type="button" class="btn btn-secondary" id="cancel-rename">Cancel</button>
            <button type="submit" class="btn btn-primary" id="save-rename">Save Changes</button>
          </div>
        </form>
      </div>
    `

    document.body.appendChild(dialog)

    const nameInput = dialog.querySelector('#project-name') as HTMLInputElement
    const form = dialog.querySelector('.project-dialog-form') as HTMLFormElement
    const closeBtn = dialog.querySelector('.project-dialog-close') as HTMLButtonElement
    const cancelBtn = dialog.querySelector('#cancel-rename') as HTMLButtonElement

    // Focus and select all text
    setTimeout(() => {
      nameInput.focus()
      nameInput.select()
    }, 100)

    // Close handlers
    const closeDialog = () => {
      document.body.removeChild(dialog)
    }

    closeBtn.addEventListener('click', closeDialog)
    cancelBtn.addEventListener('click', closeDialog)
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) closeDialog()
    })

    // Form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault()
      const newName = nameInput.value.trim()

      if (!newName || newName === project.name) {
        closeDialog()
        return
      }

      try {
        closeDialog()
        this.setLoading(true)
        const updatedProject = await this.apiService.updateProject(project.id, { 
          name: newName
        })
        
        // Update project in the list
        const projectIndex = this.projects.findIndex(p => p.id === project.id)
        if (projectIndex !== -1) {
          this.projects[projectIndex] = updatedProject
        }
        
        // Update current project if it's the one being renamed
        if (this.appState.getCurrentProject()?.id === project.id) {
          this.appState.setCurrentProject(updatedProject)
        }
        
        this.render()
        this.toastService.success('Project Renamed', `Project renamed to "${newName}"`)
      } catch (error: any) {
        console.error('Failed to rename project:', error)
        
        // Handle specific error types
        if (error.response?.status === 400) {
          const errorDetail = error.response?.data?.detail || ''
          if (errorDetail.includes('already exists')) {
            this.toastService.error('Project Name Taken', `A project named "${newName}" already exists. Please choose a different name.`)
          } else {
            this.toastService.error('Invalid Project Data', errorDetail || 'Please check your input and try again')
          }
        } else if (error.response?.status === 401) {
          this.toastService.error('Authentication Required', 'Please log in to rename projects')
        } else if (error.response?.status >= 500) {
          this.toastService.error('Server Error', 'There was a problem on our end. Please try again later.')
        } else {
          this.toastService.error('Failed to Rename Project', 'Please check your connection and try again')
        }
      } finally {
        this.setLoading(false)
      }
    })
  }

  private showEditDescriptionDialog(project: Project): void {
    const dialog = document.createElement('div')
    dialog.className = 'project-dialog-overlay'
    dialog.innerHTML = `
      <div class="project-dialog">
        <div class="project-dialog-header">
          <h3>Edit Description</h3>
          <button class="project-dialog-close" type="button">&times;</button>
        </div>
        <form class="project-dialog-form">
          <div class="form-group">
            <label for="project-description">Description</label>
            <textarea id="project-description" rows="4" maxlength="500" placeholder="Enter project description (optional)">${project.description || ''}</textarea>
          </div>
          <div class="project-dialog-actions">
            <button type="button" class="btn btn-secondary" id="cancel-description">Cancel</button>
            <button type="submit" class="btn btn-primary" id="save-description">Save Changes</button>
          </div>
        </form>
      </div>
    `

    document.body.appendChild(dialog)

    const descriptionInput = dialog.querySelector('#project-description') as HTMLTextAreaElement
    const form = dialog.querySelector('.project-dialog-form') as HTMLFormElement
    const closeBtn = dialog.querySelector('.project-dialog-close') as HTMLButtonElement
    const cancelBtn = dialog.querySelector('#cancel-description') as HTMLButtonElement

    // Focus description input
    setTimeout(() => {
      descriptionInput.focus()
      descriptionInput.setSelectionRange(descriptionInput.value.length, descriptionInput.value.length)
    }, 100)

    // Close handlers
    const closeDialog = () => {
      document.body.removeChild(dialog)
    }

    closeBtn.addEventListener('click', closeDialog)
    cancelBtn.addEventListener('click', closeDialog)
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) closeDialog()
    })

    // Form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault()
      const newDescription = descriptionInput.value.trim()

      if (newDescription === (project.description || '')) {
        closeDialog()
        return
      }

      try {
        closeDialog()
        this.setLoading(true)
        const updatedProject = await this.apiService.updateProject(project.id, { 
          description: newDescription || undefined
        })
        
        // Update project in the list
        const projectIndex = this.projects.findIndex(p => p.id === project.id)
        if (projectIndex !== -1) {
          this.projects[projectIndex] = updatedProject
        }
        
        // Update current project if it's the one being updated
        if (this.appState.getCurrentProject()?.id === project.id) {
          this.appState.setCurrentProject(updatedProject)
        }
        
        this.render()
        this.toastService.success('Description Updated', 
          newDescription ? 'Project description updated successfully' : 'Project description removed')
      } catch (error: any) {
        console.error('Failed to update description:', error)
        
        // Handle specific error types
        if (error.response?.status === 400) {
          const errorDetail = error.response?.data?.detail || ''
          this.toastService.error('Invalid Project Data', errorDetail || 'Please check your input and try again')
        } else if (error.response?.status === 401) {
          this.toastService.error('Authentication Required', 'Please log in to update project descriptions')
        } else if (error.response?.status >= 500) {
          this.toastService.error('Server Error', 'There was a problem on our end. Please try again later.')
        } else {
          this.toastService.error('Failed to Update Description', 'Please check your connection and try again')
        }
      } finally {
        this.setLoading(false)
      }
    })
  }

  private showDuplicateProjectDialog(project: Project): void {
    const dialog = document.createElement('div')
    dialog.className = 'project-dialog-overlay'
    dialog.innerHTML = `
      <div class="project-dialog">
        <div class="project-dialog-header">
          <h3>Duplicate Project</h3>
          <button class="project-dialog-close" type="button">&times;</button>
        </div>
        <form class="project-dialog-form">
          <div class="form-group">
            <label for="project-name">New Project Name *</label>
            <input type="text" id="project-name" required maxlength="100" value="${project.name} (Copy)" placeholder="Enter new project name">
          </div>
          <div class="form-group">
            <label for="project-description">Description</label>
            <textarea id="project-description" rows="3" maxlength="500" placeholder="Enter description (optional)">Copy of: ${project.description || project.name}</textarea>
          </div>
          <div class="project-dialog-actions">
            <button type="button" class="btn btn-secondary" id="cancel-duplicate">Cancel</button>
            <button type="submit" class="btn btn-primary" id="create-duplicate">Duplicate Project</button>
          </div>
        </form>
      </div>
    `

    document.body.appendChild(dialog)

    const nameInput = dialog.querySelector('#project-name') as HTMLInputElement
    const descriptionInput = dialog.querySelector('#project-description') as HTMLTextAreaElement
    const form = dialog.querySelector('.project-dialog-form') as HTMLFormElement
    const closeBtn = dialog.querySelector('.project-dialog-close') as HTMLButtonElement
    const cancelBtn = dialog.querySelector('#cancel-duplicate') as HTMLButtonElement

    // Focus and select name input
    setTimeout(() => {
      nameInput.focus()
      nameInput.select()
    }, 100)

    // Close handlers
    const closeDialog = () => {
      document.body.removeChild(dialog)
    }

    closeBtn.addEventListener('click', closeDialog)
    cancelBtn.addEventListener('click', closeDialog)
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) closeDialog()
    })

    // Form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault()
      const newName = nameInput.value.trim()
      const description = descriptionInput.value.trim()

      if (!newName) {
        nameInput.focus()
        return
      }

      try {
        closeDialog()
        this.setLoading(true)
        const newProject = await this.apiService.duplicateProject(project.id, {
          name: newName,
          description: description || undefined
        })
        
        this.projects.unshift(newProject)
        this.selectProject(newProject)
        this.toastService.success('Project Duplicated', `"${newName}" was created with all PDFs, selections, and settings`)
        
        // Show additional info about what was copied
        if (newProject.document_count > 0 || newProject.selection_count > 0) {
          this.toastService.info('Copied Successfully', 
            `${newProject.document_count} document${newProject.document_count !== 1 ? 's' : ''} and ${newProject.selection_count} selection${newProject.selection_count !== 1 ? 's' : ''} copied`)
        }
      } catch (error) {
        console.error('Failed to duplicate project:', error)
        this.toastService.error('Failed to duplicate project', 'Please try again')
      } finally {
        this.setLoading(false)
      }
    })
  }

  private showDeleteConfirmDialog(project: Project): void {
    const dialog = document.createElement('div')
    dialog.className = 'project-dialog-overlay'
    dialog.innerHTML = `
      <div class="project-dialog">
        <div class="project-dialog-header">
          <h3>Delete Project</h3>
          <button class="project-dialog-close" type="button">&times;</button>
        </div>
        <div class="project-dialog-form">
          <div class="alert alert-danger">
            <div class="alert-icon">⚠️</div>
            <div class="alert-content">
              <h4>Are you sure you want to delete "${project.name}"?</h4>
              <p>This will permanently delete:</p>
              <ul>
                <li>${project.document_count} document${project.document_count !== 1 ? 's' : ''}</li>
                <li>${project.selection_count} text selection${project.selection_count !== 1 ? 's' : ''}</li>
                <li>All project settings and data</li>
              </ul>
              <p><strong>This action cannot be undone.</strong></p>
            </div>
          </div>
          <div class="project-dialog-actions">
            <button type="button" class="btn btn-secondary" id="cancel-delete">Cancel</button>
            <button type="button" class="btn btn-danger" id="confirm-delete">Delete Project</button>
          </div>
        </div>
      </div>
    `

    document.body.appendChild(dialog)

    const closeBtn = dialog.querySelector('.project-dialog-close') as HTMLButtonElement
    const cancelBtn = dialog.querySelector('#cancel-delete') as HTMLButtonElement
    const confirmBtn = dialog.querySelector('#confirm-delete') as HTMLButtonElement

    // Close handlers
    const closeDialog = () => {
      document.body.removeChild(dialog)
    }

    closeBtn.addEventListener('click', closeDialog)
    cancelBtn.addEventListener('click', closeDialog)
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) closeDialog()
    })

    // Confirm deletion
    confirmBtn.addEventListener('click', async () => {
      try {
        closeDialog()
        this.setLoading(true)
        await this.apiService.deleteProject(project.id)
        
        this.projects = this.projects.filter(p => p.id !== project.id)
        
        // Clear current project if it was deleted
        if (this.appState.getCurrentProject()?.id === project.id) {
          this.appState.setCurrentProject(null)
          this.appState.setCurrentFile(null)
        }
        
        this.render()
        this.toastService.success('Project Deleted', `"${project.name}" was deleted successfully`)
      } catch (error) {
        console.error('Failed to delete project:', error)
        this.toastService.error('Failed to delete project', 'Please try again')
      } finally {
        this.setLoading(false)
      }
    })
  }

  private showCreateProjectDialog(): void {
    const dialog = document.createElement('div')
    dialog.className = 'project-dialog-overlay'
    dialog.innerHTML = `
      <div class="project-dialog">
        <div class="project-dialog-header">
          <h3>Create New Project</h3>
          <button class="project-dialog-close" type="button">&times;</button>
        </div>
        <form class="project-dialog-form">
          <div class="form-group">
            <label for="project-name">Project Name *</label>
            <input type="text" id="project-name" required maxlength="100" placeholder="Enter project name">
          </div>
          <div class="form-group">
            <label for="project-description">Description (Optional)</label>
            <textarea id="project-description" rows="3" maxlength="500" placeholder="Brief description of the project"></textarea>
          </div>
          <div class="project-dialog-actions">
            <button type="button" class="btn btn-secondary" id="cancel-project">Cancel</button>
            <button type="submit" class="btn btn-primary" id="create-project">Create Project</button>
          </div>
        </form>
      </div>
    `

    document.body.appendChild(dialog)

    const nameInput = dialog.querySelector('#project-name') as HTMLInputElement
    const descriptionInput = dialog.querySelector('#project-description') as HTMLTextAreaElement
    const form = dialog.querySelector('.project-dialog-form') as HTMLFormElement
    const closeBtn = dialog.querySelector('.project-dialog-close') as HTMLButtonElement
    const cancelBtn = dialog.querySelector('#cancel-project') as HTMLButtonElement

    // Focus name input
    setTimeout(() => nameInput.focus(), 100)

    // Close handlers
    const closeDialog = () => {
      document.body.removeChild(dialog)
    }

    closeBtn.addEventListener('click', closeDialog)
    cancelBtn.addEventListener('click', closeDialog)
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) closeDialog()
    })

    // Form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault()
      const name = nameInput.value.trim()
      const description = descriptionInput.value.trim()

      if (!name) {
        nameInput.focus()
        return
      }

      closeDialog()
      await this.createProject(name, description || undefined)
    })
  }

  private render(): void {
    const currentProject = this.appState.getCurrentProject()
    
    this.container.innerHTML = `
      <div class="project-manager">
        <div class="project-manager-header">
          <h2>Projects</h2>
          <button class="btn btn-primary btn-sm" id="create-project-btn" ${this.isLoading ? 'disabled' : ''}>
            + New Project
          </button>
        </div>
        
        ${this.isLoading ? `
          <div class="project-loading">
            <div class="spinner"></div>
            <p>Loading projects...</p>
          </div>
        ` : ''}
        
        ${this.projects.length === 0 && !this.isLoading ? `
          <div class="project-empty-state">
            <div class="empty-icon">📁</div>
            <h3>No Projects Yet</h3>
            <p>Create your first project to get started organizing your PDFs and text selections.</p>
            <button class="btn btn-primary" id="create-first-project-btn">Create First Project</button>
          </div>
        ` : ''}
        
        ${this.projects.length > 0 ? `
          <div class="project-list">
            ${this.projects.map(project => {
              // Use current project data if this is the active project, otherwise use cached data
              const displayProject = currentProject?.id === project.id ? currentProject : project
              return `
              <div class="project-card ${currentProject?.id === project.id ? 'active' : ''}" data-project-id="${project.id}">
                <div class="project-card-content">
                  <div class="project-card-header">
                    <h3 class="project-name">${project.name}</h3>
                    <div class="project-actions">
                      <div class="dropdown">
                        <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-project-id="${project.id}">
                          Actions ▼
                        </button>
                        <div class="dropdown-menu" data-project-id="${project.id}">
                          <button class="dropdown-item" data-action="rename" data-project-id="${project.id}">
                            📝 Rename Project
                          </button>
                          <button class="dropdown-item" data-action="editDescription" data-project-id="${project.id}">
                            📄 Edit Description
                          </button>
                          <div class="dropdown-divider"></div>
                          <button class="dropdown-item" data-action="duplicate" data-project-id="${project.id}">
                            📋 Duplicate Project
                          </button>
                          <div class="dropdown-divider"></div>
                          <button class="dropdown-item text-danger" data-action="delete" data-project-id="${project.id}">
                            🗑️ Delete Project
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  ${project.description ? `<p class="project-description">${project.description}</p>` : ''}
                  <div class="project-stats">
                    <span class="stat">📄 ${displayProject.document_count} Documents</span>
                    <span class="stat">✂️ ${displayProject.selection_count} Selections</span>
                    <span class="stat">📅 ${new Date(project.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            `
            }).join('')}
          </div>
        ` : ''}
      </div>
    `

    this.attachEventListeners()
  }

  private attachEventListeners(): void {
    // Create project buttons
    const createBtn = this.container.querySelector('#create-project-btn') as HTMLButtonElement
    const createFirstBtn = this.container.querySelector('#create-first-project-btn') as HTMLButtonElement
    
    if (createBtn) {
      createBtn.addEventListener('click', () => this.showCreateProjectDialog())
    }
    
    if (createFirstBtn) {
      createFirstBtn.addEventListener('click', () => this.showCreateProjectDialog())
    }

    // Project selection
    const projectCards = this.container.querySelectorAll('.project-card')
    projectCards.forEach(card => {
      card.addEventListener('click', (e) => {
        const target = e.target as HTMLElement
        if (target.closest('.project-actions')) return // Don't select if clicking actions
        
        const projectId = card.getAttribute('data-project-id')
        const project = this.projects.find(p => p.id === projectId)
        if (project) {
          this.selectProject(project)
        }
      })
    })

    // Dropdown toggles
    const dropdownToggles = this.container.querySelectorAll('.dropdown-toggle')
    dropdownToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.stopPropagation()
        
        // Close all other dropdowns
        const allMenus = this.container.querySelectorAll('.dropdown-menu')
        allMenus.forEach(menu => {
          if (menu !== toggle.nextElementSibling) {
            menu.classList.remove('show')
          }
        })
        
        // Toggle current dropdown
        const menu = toggle.nextElementSibling as HTMLElement
        menu.classList.toggle('show')
      })
    })

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.container.contains(e.target as Node)) {
        const allMenus = this.container.querySelectorAll('.dropdown-menu')
        allMenus.forEach(menu => menu.classList.remove('show'))
      }
    })

    // Action buttons (rename, duplicate, delete)
    const actionButtons = this.container.querySelectorAll('.dropdown-item[data-action]')
    actionButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        
        // Close dropdown
        const dropdown = btn.closest('.dropdown-menu') as HTMLElement
        dropdown.classList.remove('show')
        
        const action = btn.getAttribute('data-action')
        const projectId = btn.getAttribute('data-project-id')
        const project = this.projects.find(p => p.id === projectId)
        
        if (project) {
          switch (action) {
            case 'rename':
              this.renameProject(project)
              break
            case 'editDescription':
              this.editDescription(project)
              break
            case 'duplicate':
              this.duplicateProject(project)
              break
            case 'delete':
              this.deleteProject(project)
              break
          }
        }
      })
    })
  }

  // Public methods
  refresh(): void {
    this.loadProjects()
  }

  getCurrentProject(): Project | null {
    return this.appState.getCurrentProject()
  }
} 